/**
 * Partagé entre les pages "Détail patient" établissement et professionnel : édition de
 * `informationsMedicales` via `updateDossierMedicalInfoPro` (PUT /dossiers-medicaux/:nip,
 * accepté pour un appelant avec lien actif + identité vérifiée — voir doc backend
 * 5-RESTRICTION_SOUS_VALIDATION_COMPTE.../7.1).
 */

/** Système ABO/Rhésus — ensemble fermé, pas de valeur libre. */
export const BLOOD_GROUP_OPTIONS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
/** Phénotypes d'électrophorèse de l'hémoglobine usuels (dépistage drépanocytaire). */
export const ELECTROPHORESE_OPTIONS = ["AA", "AS", "SS", "AC", "SC", "CC"];
/** Sentinelle Radix (`SelectItem` refuse une `value` vide) pour représenter "non renseigné". */
export const UNSET_OPTION = "__non_defini__";

/** Inclut la valeur courante si elle existe mais ne fait pas partie des options standards —
 * évite d'effacer silencieusement une donnée historique atypique à l'ouverture du formulaire. */
export function withCurrentValue(options: string[], current: string): string[] {
  if (!current || options.includes(current)) return options;
  return [...options, current];
}

/** Antécédent complet (préserve `professionnel`/`date`, perdus dans les vues aplaties en lecture seule). */
export type RawAntecedent = { type: string; description: string; date: string | null; professionnel: string };

/** Copie fidèle de `informationsMedicales`, utilisée pour l'édition (évite de perdre des champs
 * non affichés en lecture seule lors du renvoi au PUT). */
export type RawMedFile = {
  poids: number | null;
  taille: number | null;
  groupeSanguin: string;
  electrophorese: string;
  allergies: string[];
  intolerances: string[];
  antecedents: RawAntecedent[];
  /** Non éditée par ce formulaire (gérée par l'onglet Vaccinations) — repassée telle quelle au PUT. */
  vaccinations: unknown[];
};

export function extractRawMedFile(raw: unknown): RawMedFile {
  const m = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const im = (m.informationsMedicales as Record<string, unknown> | undefined) ?? {};
  const toStrArray = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x)) : []);
  const toAntecedents = (v: unknown): RawAntecedent[] =>
    Array.isArray(v)
      ? v.map((x) => {
          const o = (x && typeof x === "object" ? x : {}) as Record<string, unknown>;
          return {
            type: typeof o.type === "string" ? o.type : "",
            description: typeof o.description === "string" ? o.description : "",
            date: typeof o.date === "string" ? o.date : null,
            professionnel: typeof o.professionnel === "string" ? o.professionnel : "",
          };
        })
      : [];
  return {
    poids: typeof im.poids === "number" ? im.poids : null,
    taille: typeof im.taille === "number" ? im.taille : null,
    groupeSanguin: String(im.groupeSanguin ?? ""),
    electrophorese: String(im.electrophorese ?? ""),
    allergies: toStrArray(im.allergies),
    intolerances: toStrArray(im.intolerances),
    antecedents: toAntecedents(im.antecedents),
    vaccinations: Array.isArray(im.vaccinations) ? im.vaccinations : [],
  };
}

export function emptyAntecedent(): RawAntecedent {
  return { type: "", description: "", date: null, professionnel: "" };
}

export function dateInputValue(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}
